import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, Zap, Phone, CheckCircle, AlertTriangle, Info, Battery, Clock, XCircle } from 'lucide-react';
import useSEO from '../../hooks/useSEO';

const ArrancarCocheConPinzas = () => {
  const navigate = useNavigate();

  // SEO: Set canonical to avoid duplicate content issues
  useSEO({
    title: 'Cómo Arrancar el Coche con Pinzas Paso a Paso | ArrancaYa BCN',
    description: 'Guía completa para arrancar tu coche con cables de arranque (pinzas). Tutorial paso a paso con consejos de seguridad. Si fallas, ArrancaYa BCN te ayuda 24/7 en Barcelona.',
    canonical: 'https://arrancaya-bcn.es/blog/arrancar-coche-con-pinzas'
  });

  useEffect(() => {
    // Meta keywords para SEO
    const metaKeywords = document.querySelector('meta[name="keywords"]') || document.createElement('meta');
    metaKeywords.name = 'keywords';
    metaKeywords.content = 'arrancar coche con pinzas, cables de arranque, batería descargada, como usar pinzas coche, arranque batería, Barcelona';
    document.head.appendChild(metaKeywords);

    // Schema markup para artículo
    const schema = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "Cómo Arrancar el Coche con Pinzas Paso a Paso",
      "description": "Tutorial completo para arrancar un coche con cables de arranque cuando la batería está descargada",
      "image": "https://arrancaya-bcn.es/images/hero-arranque.jpg",
      "totalTime": "PT10M",
      "estimatedCost": "0 EUR",
      "supply": [
        {
          "@type": "HowToSupply",
          "name": "Cables de arranque (pinzas)"
        },
        {
          "@type": "HowToSupply", 
          "name": "Coche con batería cargada"
        }
      ],
      "tool": [
        {
          "@type": "HowToTool",
          "name": "Guantes de seguridad"
        }
      ],
      "step": [
        {
          "@type": "HowToStep",
          "name": "Posicionar los coches",
          "text": "Acerca el coche auxiliar al tuyo sin que se toquen. Apaga ambos motores y activa el freno de mano."
        },
        {
          "@type": "HowToStep", 
          "name": "Identificar polos",
          "text": "Localiza el polo positivo (+) y negativo (-) en ambas baterías. Límpia si hay corrosión."
        },
        {
          "@type": "HowToStep",
          "name": "Conectar cable rojo",
          "text": "Conecta el cable rojo al polo positivo de la batería descargada, luego al positivo del coche auxiliar."
        },
        {
          "@type": "HowToStep",
          "name": "Conectar cable negro", 
          "text": "Conecta el cable negro al polo negativo del coche auxiliar, luego a masa metálica del coche averiado."
        },
        {
          "@type": "HowToStep",
          "name": "Arrancar",
          "text": "Arranca el coche auxiliar, espera 2-3 minutos, luego intenta arrancar el coche averiado."
        },
        {
          "@type": "HowToStep",
          "name": "Desconectar",
          "text": "Una vez arrancado, desconecta en orden inverso: negro masa, negro auxiliar, rojo auxiliar, rojo averiado."
        }
      ],
      "author": {
        "@type": "Organization",
        "name": "ArrancaYa BCN"
      },
      "datePublished": "2026-01-15",
      "dateModified": "2026-01-15"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    // GTM tracking
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'page_view',
      'page_title': 'Tutorial: Arrancar coche con pinzas',
      'page_location': window.location.href,
      'content_group1': 'Blog',
      'content_group2': 'Tutorial'
    });

    return () => {
      document.head.removeChild(script);
      if (metaKeywords.parentNode) {
        document.head.removeChild(metaKeywords);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Navigation avec fond flou */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to="/blog" 
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors group"
          >
            <ArrowLeft className="h-4 w-4 mr-2 transition-transform group-hover:-translate-x-1" />
            ← Volver al Blog
          </Link>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header avec design moderne - SANS IMAGE */}
        <header className="mb-12">
          <div className="flex items-center space-x-4 text-sm text-gray-500 mb-6">
            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full font-medium">Tutorial</span>
            <span>•</span>
            <span>15 de enero, 2026</span>
            <span>•</span>
            <span>7 min de lectura</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-8 leading-tight">
            Cómo Arrancar el Coche con 
            <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent"> Pinzas</span>
          </h1>
          
          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl p-8 border-2 border-blue-100 mb-8">
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
              <strong className="text-gray-900">Guía profesional completa</strong> para arrancar tu coche con cables de arranque cuando tienes la <strong className="text-blue-600">batería descargada</strong>. Aprende la técnica exacta que usan nuestros técnicos de ArrancaYa BCN.
            </p>
          </div>
          
          {/* Resumen ejecutivo en lugar de imagen */}
          <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">📋</span>
              Resumen: Lo que aprenderás
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-bold text-gray-900 mb-3 text-lg">✅ Técnicas Profesionales</h3>
                <ul className="text-gray-700 space-y-2">
                  <li>• Conexión segura paso a paso</li>
                  <li>• Orden correcto de cables</li>
                  <li>• Qué hacer si no arranca</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-3 text-lg">⚠️ Seguridad Garantizada</h3>
                <ul className="text-gray-700 space-y-2">
                  <li>• Precauciones obligatorias</li>
                  <li>• Errores que evitar</li>
                  <li>• Cuándo llamar a profesionales</li>
                </ul>
              </div>
            </div>
          </div>
        </header>

        {/* Contenu avec mise en page moderne - FOCUS SUR LE TEXTE */}
        <div className="prose prose-xl max-w-none space-y-12">
          
          {/* Introducción optimizada pour SEO */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-10 text-white shadow-xl">
            <div className="flex items-start">
              <div className="bg-white/20 rounded-full p-4 mr-8 flex-shrink-0">
                <Info className="h-10 w-10 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-6 text-white">¿Tu coche no arranca por batería descargada?</h2>
                <p className="text-blue-100 text-xl leading-relaxed mb-0">
                  Este tutorial te enseña cómo <strong className="text-white">arrancar el coche con pinzas</strong> de forma segura paso a paso. 
                  Si no tienes cables o no te sientes seguro, <strong className="text-yellow-300">llámanos al 613 076 099</strong> - llegamos en menos de 30 minutos a Barcelona.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-12">
            <h2 className="flex items-center text-3xl font-bold text-gray-900 mb-6">
              <div className="bg-green-100 rounded-full p-3 mr-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              ¿Qué Necesitas Para Arrancar con Pinzas?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="bg-blue-500 rounded-full p-2 mr-4 flex-shrink-0">
                    <Zap className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Cables de arranque</h3>
                    <p className="text-gray-600 text-sm">(pinzas) en buen estado</p>
                  </div>
                </div>
                
                <div className="flex items-center p-4 bg-green-50 rounded-xl border border-green-100">
                  <div className="bg-green-500 rounded-full p-2 mr-4 flex-shrink-0">
                    <Battery className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Coche auxiliar</h3>
                    <p className="text-gray-600 text-sm">Con batería cargada (12V como el tuyo)</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center p-4 bg-yellow-50 rounded-xl border border-yellow-100">
                  <div className="bg-yellow-500 rounded-full p-2 mr-4 flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Guantes de seguridad</h3>
                    <p className="text-gray-600 text-sm">(recomendado)</p>
                  </div>
                </div>
                
                <div className="flex items-center p-4 bg-purple-50 rounded-xl border border-purple-100">
                  <div className="bg-purple-500 rounded-full p-2 mr-4 flex-shrink-0">
                    <Phone className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Linterna</h3>
                    <p className="text-gray-600 text-sm">Si es de noche</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-50 to-red-100 border-l-8 border-red-500 rounded-xl p-8 my-12 shadow-lg">
            <div className="flex items-start">
              <div className="bg-red-500 rounded-full p-3 mr-6 flex-shrink-0">
                <AlertTriangle className="h-8 w-8 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-red-900 mb-4 flex items-center">
                  ⚠️ IMPORTANTE: Seguridad Antes de Empezar
                </h3>
                <div className="bg-white/50 rounded-xl p-6">
                  <ul className="text-red-800 space-y-3 text-lg font-medium">
                    <li className="flex items-center">
                      <span className="bg-red-200 rounded-full p-1 mr-3 flex-shrink-0">
                        <XCircle className="h-4 w-4 text-red-700" />
                      </span>
                      Nunca toques los extremos de los cables entre sí
                    </li>
                    <li className="flex items-center">
                      <span className="bg-red-200 rounded-full p-1 mr-3 flex-shrink-0">
                        <XCircle className="h-4 w-4 text-red-700" />
                      </span>
                      Asegúrate que ambos coches sean de 12V
                    </li>
                    <li className="flex items-center">
                      <span className="bg-red-200 rounded-full p-1 mr-3 flex-shrink-0">
                        <XCircle className="h-4 w-4 text-red-700" />
                      </span>
                      No fumes cerca de las baterías
                    </li>
                    <li className="flex items-center">
                      <span className="bg-red-200 rounded-full p-1 mr-3 flex-shrink-0">
                        <XCircle className="h-4 w-4 text-red-700" />
                      </span>
                      Si ves grietas en la batería, NO intentes arrancar
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden mb-12">
            <div className="bg-gradient-to-r from-green-600 to-blue-600 px-8 py-6">
              <h2 className="text-3xl font-bold text-white mb-2">🔧 Paso a Paso: Cómo Arrancar con Cables</h2>
              <p className="text-green-100">Sigue estos pasos exactamente en este orden</p>
            </div>

            <div className="p-8 space-y-8">
              {/* Paso 1 */}
              <div className="flex items-start space-x-6">
                <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl flex-shrink-0 shadow-lg">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Posicionar los Coches</h3>
                  <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                        Acerca el coche auxiliar hasta que los cables lleguen cómodamente
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                        Los coches <strong>NO deben tocarse</strong>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                        Apaga ambos motores y quita las llaves
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                        Activa el freno de mano en ambos vehículos
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Paso 2 */}
              <div className="flex items-start space-x-6">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl flex-shrink-0 shadow-lg">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Localizar los Polos de la Batería</h3>
                  <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-center p-3 bg-red-100 rounded-lg">
                        <span className="bg-red-500 text-white font-bold rounded-full w-8 h-8 flex items-center justify-center mr-3">+</span>
                        <span className="font-semibold text-red-700">Polo POSITIVO</span>
                      </div>
                      <div className="flex items-center p-3 bg-gray-100 rounded-lg">
                        <span className="bg-gray-600 text-white font-bold rounded-full w-8 h-8 flex items-center justify-center mr-3">-</span>
                        <span className="font-semibold text-gray-700">Polo NEGATIVO</span>
                      </div>
                    </div>
                    <p className="text-gray-600 mt-4">Si hay corrosión (polvo blanco), límpia con un trapo seco</p>
                  </div>
                </div>
              </div>

              {/* Paso 3 */}
              <div className="flex items-start space-x-6">
                <div className="bg-gradient-to-br from-red-500 to-red-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl flex-shrink-0 shadow-lg">
                  3
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Conectar el Cable Rojo (Positivo)</h3>
                  <div className="bg-red-50 rounded-xl p-6 border border-red-100">
                    <div className="bg-white/80 rounded-lg p-4 mb-4">
                      <p className="text-red-800 font-bold text-center text-lg">
                        🔴 Cable ROJO siempre va a POSITIVO (+)
                      </p>
                    </div>
                    <ol className="space-y-2 text-gray-700">
                      <li className="flex items-center">
                        <span className="bg-red-200 rounded-full w-6 h-6 flex items-center justify-center text-red-700 font-bold mr-3 flex-shrink-0">1</span>
                        Conecta una pinza roja al <strong>polo positivo (+)</strong> de la batería descargada
                      </li>
                      <li className="flex items-center">
                        <span className="bg-red-200 rounded-full w-6 h-6 flex items-center justify-center text-red-700 font-bold mr-3 flex-shrink-0">2</span>
                        Conecta la otra pinza roja al <strong>polo positivo (+)</strong> del coche auxiliar
                      </li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Paso 4 */}
              <div className="flex items-start space-x-6">
                <div className="bg-gradient-to-br from-gray-500 to-gray-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl flex-shrink-0 shadow-lg">
                  4
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Conectar el Cable Negro (Negativo)</h3>
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4 mb-4">
                      <p className="text-yellow-800 font-bold">⚡ IMPORTANTE: La última pinza negra NO va al polo negativo de tu coche</p>
                    </div>
                    <ol className="space-y-2 text-gray-700">
                      <li className="flex items-center">
                        <span className="bg-gray-300 rounded-full w-6 h-6 flex items-center justify-center text-gray-700 font-bold mr-3 flex-shrink-0">1</span>
                        Conecta una pinza negra al <strong>polo negativo (-)</strong> del coche auxiliar
                      </li>
                      <li className="flex items-center">
                        <span className="bg-gray-300 rounded-full w-6 h-6 flex items-center justify-center text-gray-700 font-bold mr-3 flex-shrink-0">2</span>
                        Conéctala a una <strong>masa metálica</strong> (tornillo, soporte del motor) lejos de la batería
                      </li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Paso 5 */}
              <div className="flex items-start space-x-6">
                <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl flex-shrink-0 shadow-lg">
                  5
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Intentar el Arranque</h3>
                  <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
                    <div className="space-y-4">
                      <div className="flex items-center p-3 bg-white rounded-lg shadow-sm">
                        <Zap className="h-6 w-6 text-yellow-500 mr-3" />
                        <span>Arranca el coche auxiliar y déjalo funcionando</span>
                      </div>
                      <div className="flex items-center p-3 bg-white rounded-lg shadow-sm">
                        <Clock className="h-6 w-6 text-yellow-500 mr-3" />
                        <span>Espera <strong>2-3 minutos</strong> para que cargue un poco tu batería</span>
                      </div>
                      <div className="flex items-center p-3 bg-white rounded-lg shadow-sm">
                        <AlertTriangle className="h-6 w-6 text-yellow-500 mr-3" />
                        <span>Intenta arrancar tu coche (no más de 15 segundos seguidos)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Paso 6 */}
              <div className="flex items-start space-x-6">
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl flex-shrink-0 shadow-lg">
                  6
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Desconectar los Cables</h3>
                  <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                    <div className="bg-purple-100 border border-purple-300 rounded-lg p-4 mb-4">
                      <p className="text-purple-800 font-bold">🔄 MUY IMPORTANTE: Desconecta en orden INVERSO</p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center p-2 bg-white rounded">
                          <span className="bg-purple-200 rounded-full w-6 h-6 flex items-center justify-center text-purple-700 font-bold mr-2">1</span>
                          <span className="text-sm">Negro de masa de tu coche</span>
                        </div>
                        <div className="flex items-center p-2 bg-white rounded">
                          <span className="bg-purple-200 rounded-full w-6 h-6 flex items-center justify-center text-purple-700 font-bold mr-2">2</span>
                          <span className="text-sm">Negro del polo negativo auxiliar</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center p-2 bg-white rounded">
                          <span className="bg-purple-200 rounded-full w-6 h-6 flex items-center justify-center text-purple-700 font-bold mr-2">3</span>
                          <span className="text-sm">Rojo del polo positivo auxiliar</span>
                        </div>
                        <div className="flex items-center p-2 bg-white rounded">
                          <span className="bg-purple-200 rounded-full w-6 h-6 flex items-center justify-center text-purple-700 font-bold mr-2">4</span>
                          <span className="text-sm">Rojo del polo positivo de tu coche</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border-2 border-orange-200 rounded-2xl p-8 shadow-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
              <span className="bg-orange-500 text-white rounded-full w-10 h-10 flex items-center justify-center mr-4">❌</span>
              ¿Qué Hacer Si No Arranca?
            </h2>
            
            <div className="bg-orange-50 rounded-xl p-6 border border-orange-200 mb-6">
              <p className="text-orange-900 text-lg font-medium">
                Si después de varios intentos tu coche no arranca, puede ser que:
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
                  <h3 className="font-bold text-red-800 mb-2">🔋 Problemas de Batería</h3>
                  <ul className="text-red-700 space-y-1 text-sm">
                    <li>• La batería esté completamente muerta</li>
                    <li>• Batería muy vieja (más de 5 años)</li>
                    <li>• Células internas dañadas</li>
                  </ul>
                </div>
                
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
                  <h3 className="font-bold text-blue-800 mb-2">⚡ Problemas Eléctricos</h3>
                  <ul className="text-blue-700 space-y-1 text-sm">
                    <li>• Tengas un problema en el alternador</li>
                    <li>• El motor de arranque esté averiado</li>
                    <li>• Haya una avería eléctrica</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
                <h3 className="font-bold text-yellow-800 mb-4 text-lg">💡 Consejo de ArrancaYa BCN</h3>
                <p className="text-yellow-700 leading-relaxed">
                  Si no consigues arrancar en <strong>3 intentos</strong>, no sigas forzando. 
                  Puedes dañar el sistema eléctrico. Es mejor llamar a un profesional.
                </p>
                <div className="mt-4 p-3 bg-yellow-100 rounded-lg border border-yellow-300">
                  <p className="text-yellow-800 font-medium text-center">
                    📞 <strong>613 076 099</strong> - Llegamos en 30 minutos
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Section: Después del Arranque */}
          <div className="bg-white border-2 border-green-200 rounded-2xl p-8 shadow-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="bg-green-500 text-white rounded-full w-10 h-10 flex items-center justify-center mr-4">✅</span>
              Después del Arranque: Qué Hacer
            </h2>
            <div className="bg-green-50 rounded-xl p-6 border border-green-100">
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span><strong className="text-gray-900">No apagues el motor</strong> inmediatamente - déjalo funcionando</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span><strong className="text-gray-900">Conduce al menos 20-30 minutos</strong> para recargar la batería</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span><strong className="text-gray-900">Evita consumos innecesarios:</strong> aire acondicionado, radio, luces</span>
                </li>
                <li className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-yellow-500 mr-3 mt-1 flex-shrink-0" />
                  <span>Si el coche <strong className="text-red-600">vuelve a fallar</strong>, la batería puede estar agotada</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Section: Cuándo NO Intentar */}
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-2xl p-8 shadow-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="bg-red-500 text-white rounded-full w-10 h-10 flex items-center justify-center mr-4">🚫</span>
              Cuándo NO Intentar Arrancar con Pinzas
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-4 border border-red-100">
                <div className="flex items-center mb-2">
                  <XCircle className="h-5 w-5 text-red-500 mr-2" />
                  <span className="font-bold text-red-800">Batería dañada</span>
                </div>
                <p className="text-gray-600 text-sm">Si tiene grietas o está hinchada</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-red-100">
                <div className="flex items-center mb-2">
                  <XCircle className="h-5 w-5 text-red-500 mr-2" />
                  <span className="font-bold text-red-800">Humo visible</span>
                </div>
                <p className="text-gray-600 text-sm">Si sale humo de la batería</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-red-100">
                <div className="flex items-center mb-2">
                  <XCircle className="h-5 w-5 text-red-500 mr-2" />
                  <span className="font-bold text-red-800">Sin experiencia</span>
                </div>
                <p className="text-gray-600 text-sm">Si estás nervioso o inseguro</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-red-100">
                <div className="flex items-center mb-2">
                  <XCircle className="h-5 w-5 text-red-500 mr-2" />
                  <span className="font-bold text-red-800">Mal tiempo</span>
                </div>
                <p className="text-gray-600 text-sm">Condiciones meteorológicas peligrosas</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-red-100 md:col-span-2">
                <div className="flex items-center mb-2">
                  <XCircle className="h-5 w-5 text-red-500 mr-2" />
                  <span className="font-bold text-red-800">Coche híbrido o eléctrico</span>
                </div>
                <p className="text-gray-600 text-sm">Necesitas un profesional - los sistemas de alto voltaje son peligrosos</p>
              </div>
            </div>
          </div>

          {/* Section: Problemas Comunes */}
          <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 shadow-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
              <span className="bg-orange-500 text-white rounded-full w-10 h-10 flex items-center justify-center mr-4">🔧</span>
              Problemas Comunes y Soluciones
            </h2>

            <div className="space-y-6">
              {/* Problema 1 */}
              <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
                <h3 className="text-xl font-bold text-orange-800 mb-4 flex items-center">
                  <span className="bg-orange-200 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">1</span>
                  "Los Cables No Llegan"
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white p-3 rounded-lg">
                    <p className="font-semibold text-gray-800 text-sm">💡 Solución</p>
                    <p className="text-gray-600 text-sm">Acerca más el coche auxiliar o usa cables más largos</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="font-semibold text-gray-800 text-sm">📏 Mínimo</p>
                    <p className="text-gray-600 text-sm">Cables de 3 metros recomendados</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="font-semibold text-gray-800 text-sm">⚡ Tip Pro</p>
                    <p className="text-gray-600 text-sm">Cables gruesos transmiten mejor la corriente</p>
                  </div>
                </div>
              </div>

              {/* Problema 2 */}
              <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
                <h3 className="text-xl font-bold text-yellow-800 mb-4 flex items-center">
                  <span className="bg-yellow-200 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">2</span>
                  "El Motor Hace Ruido pero No Arranca"
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white p-3 rounded-lg">
                    <p className="font-semibold text-gray-800 text-sm">🔍 Causa</p>
                    <p className="text-gray-600 text-sm">Batería muy descargada</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="font-semibold text-gray-800 text-sm">💡 Solución</p>
                    <p className="text-gray-600 text-sm">Espera 5-10 min con conexión antes de intentar</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="font-semibold text-gray-800 text-sm">⚠️ Si persiste</p>
                    <p className="text-gray-600 text-sm">Puede ser el motor de arranque</p>
                  </div>
                </div>
              </div>

              {/* Problema 3 */}
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-xl font-bold text-red-800 mb-4 flex items-center">
                  <span className="bg-red-200 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">3</span>
                  "Arranca Pero Se Vuelve a Parar"
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white p-3 rounded-lg">
                    <p className="font-semibold text-gray-800 text-sm">🔍 Causa</p>
                    <p className="text-gray-600 text-sm">Problema en el alternador</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="font-semibold text-gray-800 text-sm">⚡ Acción</p>
                    <p className="text-gray-600 text-sm">No desconectes los cables aún</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="font-semibold text-gray-800 text-sm">📞 Recomendación</p>
                    <p className="text-gray-600 text-sm">Llama a ArrancaYa BCN</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section: Mantenimiento de Cables */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-2xl p-8 shadow-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
              <span className="bg-blue-500 text-white rounded-full w-10 h-10 flex items-center justify-center mr-4">🔌</span>
              Mantenimiento de Cables de Arranque
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-6 border border-blue-100">
                <h3 className="text-xl font-bold text-blue-800 mb-4">✅ Revisión Antes de Usar</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span><strong>Cables intactos</strong> sin cortes en la funda</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span><strong>Pinzas firmes</strong> que cierren bien</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span><strong>Conexiones limpias</strong> sin óxido</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span><strong>Grosor adecuado</strong> (mínimo 25mm²)</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-6 border border-purple-100">
                <h3 className="text-xl font-bold text-purple-800 mb-4">🏠 Almacenamiento</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center">
                    <span className="bg-purple-100 rounded-full w-6 h-6 flex items-center justify-center mr-2 text-sm">1</span>
                    <span>Guárdalos en <strong>lugar seco</strong></span>
                  </li>
                  <li className="flex items-center">
                    <span className="bg-purple-100 rounded-full w-6 h-6 flex items-center justify-center mr-2 text-sm">2</span>
                    <span>Evita enrollarlos <strong>muy apretados</strong></span>
                  </li>
                  <li className="flex items-center">
                    <span className="bg-purple-100 rounded-full w-6 h-6 flex items-center justify-center mr-2 text-sm">3</span>
                    <span>Revisa <strong>cada 6 meses</strong></span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Section: Tipos de Baterías */}
          <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 shadow-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="bg-purple-500 text-white rounded-full w-10 h-10 flex items-center justify-center mr-4">🔋</span>
              Tipos de Baterías y Consideraciones
            </h2>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
              <h3 className="text-blue-800 font-bold mb-4 flex items-center">
                <Info className="h-5 w-5 mr-2" />
                💡 ¿Sabías que...?
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start">
                  <span className="bg-blue-200 rounded-full w-6 h-6 flex items-center justify-center mr-2 text-xs flex-shrink-0">•</span>
                  <span className="text-blue-700">Las baterías AGM necesitan <strong>voltaje específico</strong> para cargar</span>
                </div>
                <div className="flex items-start">
                  <span className="bg-blue-200 rounded-full w-6 h-6 flex items-center justify-center mr-2 text-xs flex-shrink-0">•</span>
                  <span className="text-blue-700">Los coches modernos pueden tener <strong>2 baterías</strong></span>
                </div>
                <div className="flex items-start">
                  <span className="bg-blue-200 rounded-full w-6 h-6 flex items-center justify-center mr-2 text-xs flex-shrink-0">•</span>
                  <span className="text-blue-700">Algunas baterías tienen <strong>válvulas de seguridad</strong></span>
                </div>
                <div className="flex items-start">
                  <span className="bg-blue-200 rounded-full w-6 h-6 flex items-center justify-center mr-2 text-xs flex-shrink-0">•</span>
                  <span className="text-blue-700">Los híbridos tienen sistemas de <strong>12V y 200-300V</strong></span>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-4">Comparativa de Tipos</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-green-50 p-4 rounded-xl border border-green-200 text-center">
                <div className="text-2xl mb-2">🔋</div>
                <h4 className="font-bold text-green-800">Convencionales</h4>
                <p className="text-green-700 text-sm">Más fáciles de arrancar</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-xl border border-blue-200 text-center">
                <div className="text-2xl mb-2">⚡</div>
                <h4 className="font-bold text-blue-800">AGM</h4>
                <p className="text-blue-700 text-sm">Necesitan cargadores inteligentes</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200 text-center">
                <div className="text-2xl mb-2">💧</div>
                <h4 className="font-bold text-yellow-800">Gel</h4>
                <p className="text-yellow-700 text-sm">Muy sensibles a sobrecargas</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-xl border border-purple-200 text-center">
                <div className="text-2xl mb-2">🔌</div>
                <h4 className="font-bold text-purple-800">Litio</h4>
                <p className="text-purple-700 text-sm">Requieren equipos especiales</p>
              </div>
            </div>
          </div>

        </div>

        {/* CTA profesional */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg p-8 mt-12">
          <div className="text-center">
            <Zap className="h-12 w-12 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">¿No Te Atreves o No Tienes Cables?</h2>
            <p className="text-lg mb-6 opacity-90">
              En ArrancaYa BCN somos expertos en <strong>arranque de baterías en Barcelona</strong>. 
              Te arrancamos el coche en menos de 30 minutos, con equipo profesional y garantía.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3"
              >
                <Zap className="h-5 w-5 mr-2" />
                Solicitar Arranque Ahora
              </Button>
              <Button
                onClick={() => window.open('tel:+34613076099')}
                variant="outline"
                className="bg-white text-blue-600 border-2 border-white hover:bg-blue-50 px-8 py-3"
              >
                <Phone className="h-5 w-5 mr-2" />
                Llamar: 613 076 099
              </Button>
            </div>
            <p className="text-sm mt-4 opacity-80">
              ⭐ Servicio 24/7 • ⚡ Llegada en 30min • 💯 Solo pagas si arranca
            </p>
          </div>
        </div>
      </article>
    </div>
  );
};

export default ArrancarCocheConPinzas;